import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchInput } from '@ds';
import { AdminPageHeader, DataTable } from '@ui';
import { useAuthContext } from '@lp/core/auth';
import { getRenderRegionTableCell, REGION_TABLE_COLUMNS } from '@lp/features/admin/tables/RegionListTable';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useRegionListQuery } from '@/queries/regions';
import { AdminPermissions } from '@/permissions';

export const RegionList = () => {
  const { checkPermission } = useAuthContext();
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetching,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: {},
    query: useRegionListQuery,
  });
  const hasEditPermissions = checkPermission(AdminPermissions.RegionsUpdate);

  const handleEdit = useCallback((regionId: string) => navigate(`/admin/regions/${regionId}`), [navigate]);

  const renderRegionTableCell = useMemo(() => getRenderRegionTableCell(handleEdit), [handleEdit]);

  const REGIONS_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasEditPermissions ? REGION_TABLE_COLUMNS : REGION_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasEditPermissions]);

  return (
    <div className="flex flex-col place-items-stretch gap-9">
      <AdminPageHeader title="Regions">
        <div className="max-w-[480px] grow">
          <SearchInput placeholder="Search" value={searchText} onValueChange={setSearchText} inputVariant="secondary" />
        </div>
      </AdminPageHeader>
      <div className="flex flex-col gap-4">
        <DataTable
          label="Region List"
          columns={REGIONS_TABLE_COLUMNS_FILTERED}
          renderCell={renderRegionTableCell}
          data={data?.items || []}
          isLoading={isLoading}
          isFetching={isFetching}
          totalItems={data?.totalItems || 0}
          totalPages={data?.totalPages || 1}
          page={page}
          itemsPerPage={itemsPerPage}
          sortBy={sortBy}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
          onSortByChange={setSortBy}
        />
      </div>
    </div>
  );
};
